import { ScheduledJob } from "@/types/common.types";

import { ArrowLeftIcon, Download, MessageCircle } from "lucide-react";

import Text from "../common/Text";
import { useEffect, useState } from "react";
import { useDebounceValue } from "usehooks-ts";
import { VisibilityState } from "@tanstack/react-table";
import SearchInput from "../common/SearchInput";
import MultiSelect from "../common/MultiSelectDropdown";
import { Button } from "../ui/button";
import Icon from "../common/Icon";
import { InsightDetailForJobByDateTable } from "./table";
import { downloadFile } from "@/utils/downloadFile";
import InsightsFilterSelectionForJob, {
  FilterOption,
} from "./InsightsFilterSelectionForJob";
import { default as AverageRatingOccasionChart } from "../charts/insights/AverageRating/OccasionChart";
import { default as InventoryDistributionOccasionChart } from "../charts/insights/InventoryDistribution/OccasionChart";
import { default as RevenueDistributionOccasionChart } from "../charts/insights/RevenueDistribution/OccasionChart";
import { useGetRolesQuery } from "@/hooks/queries";
import { default as AverageValueOccasionChart } from "../charts/insights/AverageValue/OccasionChart";

import { default as AverageRatingColourChart } from "../charts/insights/AverageRating/ColourChart";
import { default as InventoryDistributionColourChart } from "../charts/insights/InventoryDistribution/ColourChart";
import { default as RevenueDistributionColourChart } from "../charts/insights/RevenueDistribution/ColourChart";
import { default as AverageValueColourChart } from "../charts/insights/AverageValue/ColourChart";

import { default as AverageRatingArticleTypeChart } from "../charts/insights/AverageRating/ArticleTypChart";
import { default as RevenueDistributionArticleTypeChart } from "../charts/insights/RevenueDistribution/ArticleTypeChart";
import { default as InventoryDistributionArticleTypeChart } from "../charts/insights/InventoryDistribution/ArticleTypeChart";
import { default as AverageValueArticleTypeChart } from "../charts/insights/AverageValue/ArticleTypChart";

import { default as AverageRatingSeasonChart } from "../charts/insights/AverageRating/SeasonChart";
import { default as InventoryDistributionSeasonChart } from "../charts/insights/InventoryDistribution/SeasonChart";
import { default as RevenueDistributionSeasonChart } from "../charts/insights/RevenueDistribution/SeasonChart";
import { default as AverageValueSeasonChart } from "../charts/insights/AverageValue/SeasonChart";

import { default as AverageRatingBrandChart } from "../charts/insights/AverageRating/BrandChart";
import { default as RevenueDistributionBrandChart } from "../charts/insights/RevenueDistribution/BrandChart";
import { default as InventoryDistributionBrandChart } from "../charts/insights/InventoryDistribution/BrandChart";
import { default as AverageValueBrandChart } from "../charts/insights/AverageValue/BrandChart";
import useAnalyticsManager from "@/hooks/miscellaneous/useAnalyticsManager";
import { useDownloadInsightsForDateQuery } from "@/hooks/queries";
import ChatInterface from "../common/ChatInterface";

type JobDetailProps = {
  job: ScheduledJob;
  date: string;
  onBack: () => void;
};

const FILTER_INSIGHTS_OPTIONS = [
  {
    value: "popularStyle",
    label: "Popular Style",
  },
  {
    value: "trendingStyle",
    label: "Trending Style",
    subOptions: [
      { value: "trendingStyle1month", label: "1 Month" },
      { value: "trendingStyle2month", label: "2 Months" },
      { value: "trendingStyle3month", label: "3 Months" },
    ],
  },
  {
    value: "averageRating",
    label: "Average Rating",
  },
  {
    value: "inventoryDistribution",
    label: "Inventory Distribution",
  },
  {
    value: "revenueDistribution",
    label: "Revenue Distribution",
  },
  {
    value: "averageValue",
    label: "Average Values",
    subOptions: [
      { value: "mrp", label: "MRP" },
      { value: "asp", label: "ASP" },
      { value: "discount", label: "Discount" },
    ],
  },
] as FilterOption[];

const InsightDetailForJobByDate: React.FC<JobDetailProps> = ({
  job,
  date,
  onBack,
}) => {
  const { sendEvent } = useAnalyticsManager();
  const { data: roleData } = useGetRolesQuery();
  const [search, setSearch] = useState("");
  const [selectedFilter, setSelectedFilter] = useState<string | null>(null);
  const [selectedSubFilter, setSelectedSubFilter] = useState<string | null>(
    null
  );
  const [debouncedSearchValue] = useDebounceValue(search, 500);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [columnOptions, setColumnOptions] = useState<
    Array<{ id: string; label: string }>
  >([]);
  const [initialSelectedColumns, setInitialSelectedColumns] = useState<
    string[]
  >([]);
  const [downloadExcel, setDownloadExcel] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const {
    data: excelData,
    isLoading: isDownloadingInsights,
    isSuccess: isSuccessInsights,
  } = useDownloadInsightsForDateQuery(job.id, date, {
    enabled: downloadExcel && job.id !== null,
  });
  const handleColumnChange = (selected: string[]) => {
    const newColumnVisibility = Object.fromEntries(
      columnOptions.map((option) => [option.id, selected.includes(option.id)])
    );
    setColumnVisibility(newColumnVisibility);
  };
  const handleSubOptionSelect = (subValue: string) => {
    setSelectedSubFilter(subValue);
    sendEvent("insight_view_selected", {
      selected_view: subValue,
    });
  };
  useEffect(() => {
    if (isSuccessInsights && downloadExcel) {
      downloadFile(excelData, `${job.jobName}.csv`);
      setDownloadExcel(false);
    }
  }, [isSuccessInsights, excelData, job.jobName, downloadExcel]);
  const onDownload = () => {
    setDownloadExcel(true);
    sendEvent("insight_export_clicked", {
      dataset_id: job.id.toString(),
      screen_name: "insight_detail",
    });
  };
  sendEvent("insight_table_screen_viewed", {
    dataset_id: job.id.toString(),
    dataset_name: job.jobName,
    insight_start_date: date,
    insight_end_date: date,
    total_styles_displayed: job.totalStyleIds || 0,
  });

  return (
    <>
      <div className="flex w-full flex-1 items-start gap-2">
        <ArrowLeftIcon
          className="mt-2 cursor-pointer text-[#1C1B1F]"
          onClick={() => {
            sendEvent("back_to_job_list_clicked", {
              job_id: job.id,
            });

            onBack();
          }}
        />
        <div className="flex w-full flex-col gap-4">
          <h1 className="font-display text-[34px] font-medium leading-[1.05] tracking-[-0.02em] text-brand-text">
            Insights.
          </h1>
          <div className="flex flex-wrap items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-brand-muted">
            <span>Insights</span>
            <span className="text-brand-border">/</span>
            <span>{job.jobName}</span>
            <span className="text-brand-border">/</span>
            <span>{date}</span>
          </div>
          <div className="flex items-center justify-end gap-4">
            <div className="flex items-center gap-4">
              <SearchInput value={search} onChange={setSearch} />
              <Button
                variant="outline"
                className="flex items-center justify-center gap-2 border-brand-border font-mono text-[11px] uppercase tracking-[0.22em] text-brand-text"
                leftIcon={<Download className="h-4 w-4" />}
                onClick={onDownload}
                isLoading={isDownloadingInsights}
              >
                Export Data
              </Button>
              <div className="flex gap-3">
                {columnOptions.length > 0 && (
                  <MultiSelect
                    options={columnOptions}
                    initialSelected={initialSelectedColumns}
                    onChange={handleColumnChange}
                    trigger={
                      <Button
                        variant="outline"
                        className="flex items-center justify-center gap-2 border-brand-border font-mono text-[11px] uppercase tracking-[0.22em] text-brand-text"
                        leftIcon={<Icon name="edit" size={16} />}
                      >
                        Edit Column
                      </Button>
                    }
                    screen_name="insights_detail"
                  />
                )}
              </div>
              <InsightsFilterSelectionForJob
                options={FILTER_INSIGHTS_OPTIONS}
                value={selectedFilter || selectedSubFilter || ""}
                onChange={setSelectedFilter}
                onSubOptionSelect={handleSubOptionSelect}
              />
            </div>
          </div>
        </div>
      </div>
      {(() => {
        if (selectedFilter === "averageRating") {
          return (
            <div className="mt-6 flex flex-col gap-6">
              <div className="flex items-center justify-center gap-6">
                <AverageRatingOccasionChart
                  jobId={job.id.toString()}
                  date={date}
                />
                <AverageRatingArticleTypeChart
                  jobId={job.id.toString()}
                  date={date}
                />
              </div>
              <div className="flex items-center justify-center gap-6">
                <AverageRatingSeasonChart
                  jobId={job.id.toString()}
                  date={date}
                />
              </div>
              <AverageRatingBrandChart jobId={job.id.toString()} date={date} />
              <AverageRatingColourChart jobId={job.id.toString()} date={date} />
            </div>
          );
        } else if (selectedFilter === "inventoryDistribution") {
          return (
            <div className="mt-6 flex flex-col gap-6">
              <div className="flex items-center justify-center gap-6">
                <InventoryDistributionOccasionChart
                  jobId={job.id.toString()}
                  date={date}
                />
                <InventoryDistributionColourChart
                  jobId={job.id.toString()}
                  date={date}
                />
                <InventoryDistributionArticleTypeChart
                  jobId={job.id.toString()}
                  date={date}
                />
              </div>
              <div className="flex items-center justify-center gap-6">
                <InventoryDistributionSeasonChart
                  jobId={job.id.toString()}
                  date={date}
                />
                <InventoryDistributionBrandChart
                  jobId={job.id.toString()}
                  date={date}
                />
              </div>
            </div>
          );
        } else if (selectedFilter === "revenueDistribution") {
          return (
            <div className="mt-6 flex flex-col gap-6">
              <div className="flex items-center justify-center gap-6">
                <RevenueDistributionOccasionChart
                  jobId={job.id.toString()}
                  date={date}
                />
                <RevenueDistributionColourChart
                  jobId={job.id.toString()}
                  date={date}
                />
                <RevenueDistributionArticleTypeChart
                  jobId={job.id.toString()}
                  date={date}
                />
              </div>
              <div className="flex items-center justify-center gap-6">
                <RevenueDistributionSeasonChart
                  jobId={job.id.toString()}
                  date={date}
                />
                <RevenueDistributionBrandChart
                  jobId={job.id.toString()}
                  date={date}
                />
              </div>
            </div>
          );
        } else if (
          selectedSubFilter === "mrp" ||
          selectedSubFilter === "asp" ||
          selectedSubFilter === "discount"
        ) {
          return (
            <div className="mt-6 flex flex-col gap-6">
              <div className="flex items-center justify-center gap-6">
                <AverageValueOccasionChart
                  jobId={job.id.toString()}
                  date={date}
                  valueType={selectedSubFilter}
                />
                <AverageValueArticleTypeChart
                  jobId={job.id.toString()}
                  date={date}
                  valueType={selectedSubFilter}
                />
              </div>
              <div className="flex items-center justify-center gap-6">
                <AverageValueSeasonChart
                  jobId={job.id.toString()}
                  date={date}
                  valueType={selectedSubFilter}
                />
                <AverageValueBrandChart
                  jobId={job.id.toString()}
                  date={date}
                  valueType={selectedSubFilter}
                />
              </div>
              <AverageValueColourChart
                jobId={job.id.toString()}
                date={date}
                valueType={selectedSubFilter}
              />
            </div>
          );
        } else if (selectedSubFilter === "trendingStyle1month") {
          return (
            <InsightDetailForJobByDateTable
              job={job}
              date={date}
              selectedOptions={selectedSubFilter || ""}
              search={search}
              setSearch={setSearch}
              debouncedSearchValue={debouncedSearchValue}
              columnVisibility={columnVisibility}
              setColumnVisibility={setColumnVisibility}
              setColumnOptions={setColumnOptions}
              setInitialSelectedColumns={setInitialSelectedColumns}
            />
          );
        } else if (selectedSubFilter === "trendingStyle2month") {
          return (
            <InsightDetailForJobByDateTable
              job={job}
              date={date}
              selectedOptions={selectedSubFilter || ""}
              search={search}
              setSearch={setSearch}
              debouncedSearchValue={debouncedSearchValue}
              columnVisibility={columnVisibility}
              setColumnVisibility={setColumnVisibility}
              setColumnOptions={setColumnOptions}
              setInitialSelectedColumns={setInitialSelectedColumns}
            />
          );
        } else if (selectedSubFilter === "trendingStyle3month") {
          return (
            <InsightDetailForJobByDateTable
              job={job}
              date={date}
              selectedOptions={selectedSubFilter || ""}
              search={search}
              setSearch={setSearch}
              debouncedSearchValue={debouncedSearchValue}
              columnVisibility={columnVisibility}
              setColumnVisibility={setColumnVisibility}
              setColumnOptions={setColumnOptions}
              setInitialSelectedColumns={setInitialSelectedColumns}
            />
          );
        } else {
          return (
            <InsightDetailForJobByDateTable
              job={job}
              date={date}
              selectedOptions={selectedFilter || ""}
              search={search}
              setSearch={setSearch}
              debouncedSearchValue={debouncedSearchValue}
              columnVisibility={columnVisibility}
              setColumnVisibility={setColumnVisibility}
              setColumnOptions={setColumnOptions}
              setInitialSelectedColumns={setInitialSelectedColumns}
            />
          );
        }
      })()}

      {(roleData?.isSuperAdmin || roleData?.isAdmin) && (
        <button
          onClick={() => {
            setIsChatOpen(true);
            sendEvent("ai_chat_opened", {
              dataset_id: job.id.toString(),
              screen_name: "insight_detail",
            });
          }}
          className="fixed bottom-6 right-6 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-blue-600 text-white shadow-lg transition-all hover:bg-blue-700 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          aria-label="Open AI Chat"
        >
          <MessageCircle className="h-6 w-6" />
        </button>
      )}

      <ChatInterface
        isOpen={isChatOpen}
        onClose={() => {
          setIsChatOpen(false);
          sendEvent("ai_chat_closed", {
            dataset_id: job.id.toString(),
            screen_name: "insight_detail",
          });
        }}
        insightId={date}
        scheduledJobId={job.id.toString()}
      />
    </>
  );
};

export default InsightDetailForJobByDate;
